/**
 * Impressão de documentos da clínica.
 *
 * Margens de impressão vão em @page para repetirem em cada folha (padding no
 * html só afeta a primeira/última página e corta o conteúdo na quebra).
 * Com margem @page > 0, o Chrome pode exibir "Cabeçalhos e rodapés" no diálogo;
 * deixe desmarcado para um PDF limpo.
 */

import { nativePrintHtml } from './nativeApp';

export interface ClinicPrintOptions {
  /** Título da aba. Se omitido, mantém o <title> do próprio HTML. */
  title?: string;
  /** false abre apenas para visualização, sem disparar a impressão. */
  print?: boolean;
}

const PRINT_RESET_STYLE = `<style data-klinvia-print-reset>
@page { size: A4; margin: 12mm 12mm 14mm 12mm; }
@media print {
  html, body { margin: 0; padding: 0; box-sizing: border-box; background: #fff; }
}
</style>`;

const escapeTitle = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Injeta o reset de @page, ajusta o título e remove auto-print duplicado. */
export function preparePrintHtml(html: string, options?: ClinicPrintOptions): string {
  let out = html;

  out = out.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, (block) =>
    /window\.print|onafterprint/i.test(block) ? '' : block,
  );

  if (options?.title !== undefined) {
    const titleTag = `<title>${escapeTitle(options.title)}</title>`;
    out = /<title[^>]*>[\s\S]*?<\/title>/i.test(out)
      ? out.replace(/<title[^>]*>[\s\S]*?<\/title>/i, titleTag)
      : out.replace(/<head([^>]*)>/i, `<head$1>${titleTag}`);
  }

  if (/<\/body>/i.test(out)) {
    out = out.replace(/<\/body>/i, `${PRINT_RESET_STYLE}</body>`);
  } else if (/<\/html>/i.test(out)) {
    out = out.replace(/<\/html>/i, `${PRINT_RESET_STYLE}</html>`);
  } else {
    out += PRINT_RESET_STYLE;
  }

  return out;
}

/** Abre o documento em nova aba e dispara a impressão. Retorna false se bloqueado. */
export function printClinicHtml(html: string, options?: ClinicPrintOptions): boolean {
  const prepared = preparePrintHtml(html, options);
  const shouldPrint = options?.print !== false;

  // No app iOS não há abas: o diálogo nativo já mostra a pré-visualização.
  if (nativePrintHtml(prepared, options?.title)) return true;

  const win = window.open('', '_blank');
  if (!win) {
    return shouldPrint ? printInHiddenIframe(prepared) : false;
  }

  win.document.open();
  win.document.write(prepared);
  win.document.close();

  if (shouldPrint) {
    window.setTimeout(() => {
      try {
        win.focus();
        win.print();
      } catch {
        /* usuário pode imprimir manualmente pela aba aberta */
      }
    }, 400);
  }

  return true;
}

/** Usado quando o pop-up é bloqueado: imprime sem abrir aba. */
function printInHiddenIframe(html: string): boolean {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText =
    'position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none;';
  document.body.appendChild(iframe);

  const win = iframe.contentWindow;
  const doc = iframe.contentDocument ?? win?.document;
  if (!win || !doc) {
    iframe.remove();
    return false;
  }

  doc.open();
  doc.write(html);
  doc.close();

  const cleanup = () => {
    window.setTimeout(() => iframe.remove(), 800);
  };
  win.onafterprint = cleanup;

  window.setTimeout(() => {
    try {
      win.focus();
      win.print();
    } catch {
      cleanup();
    }
  }, 300);
  window.setTimeout(cleanup, 60_000);

  return true;
}
